import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { makeSafeRequest } from '../../common/makeSafeRequest';
import Preloader from '../../components/common/Preloader';
import { AppInfoContext } from '../../components/contexts/AppInfoContext';
import { Optional } from '../../types/common';
import { Echelon, ForcePackageData, ForcePackagesFiltersData } from '../../types/force-packages';
import { FiltersData, InputData } from '../../types/systems';
import { defaultSettings, fpSettingsTemplate } from './defaultSettings';
import { downloadForcePackageSettings } from './downloadForcePackageSettings';
import ForcePackagesPage from './force-package-page/ForcePackagesPage';

const ForcePackages: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);
	const malformedPackagePath = useRef<string[]>([]);

	const downloadFilters = useCallback(async () => {
		return await makeSafeRequest<FiltersData>('force-packages/', 'GET', {
			params: { pid: currentPid },
		});
	}, [currentPid]);

	const initialize = useCallback(async () => {
		const filters = await downloadFilters();
		const inpData = await downloadInputData();

		if (!filters || !inpData) {
			return false;
		}

		setFilters(filters);
		setInputData(inpData);
		return true;
	}, [downloadFilters]);

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('force-packages/input-data/', 'GET');
	};

	const addForcePackage = async (settings: Optional<ForcePackageData, 'id'>) => {
		if (validateSettings(settings.fp.echelons)) {
			return await makeSafeRequest<ForcePackageData>('force-packages/', 'POST', {
				payload: settings,
				params: { pid: currentPid },
				onSuccessMessage: 'Force package added successfully!',
				onErrorMessage: 'Error adding force package!',
			});
		} else {
			notifications.show({
				title: (
					<>
						<Text weight={'bold'}>Missing commanders:</Text>
						<Text>{malformedPackagePath.current.join(' → ')}</Text>
					</>
				),
				message: '',
				color: 'yellow',
				autoClose: 5000,
			});
		}
	};

	const deleteForcePackage = async (settingsId: number) => {
		return await makeSafeRequest<ForcePackageData>(`force-packages/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Force package deleted successfully!',
			onErrorMessage: 'Error deleting force package!',
		});
	};

	const saveForcePackage = async (settings: Optional<ForcePackageData, 'id'>) => {
		if (validateSettings(settings.fp.echelons)) {
			return await makeSafeRequest<ForcePackageData>(
				`force-packages/${settings.id}/`,
				'PUT',
				{
					payload: settings,
					params: { pid: currentPid },
					onSuccessMessage: 'Force package saved successfully!',
					onErrorMessage: 'Error saving force package!',
				}
			);
		} else {
			notifications.show({
				title: (
					<>
						<Text weight={'bold'}>Missing commanders:</Text>
						<Text>{malformedPackagePath.current.join(' → ')}</Text>
					</>
				),
				message: '',
				color: 'yellow',
				autoClose: 5000,
			});
		}
	};

	const duplicateForcePackage = async (settingsId: number) => {
		return await makeSafeRequest<ForcePackageData>(
			`force-packages/${settingsId}/duplicate/`,
			'POST',
			{
				onSuccessMessage: 'Force package duplicated successfully!',
				onErrorMessage: 'Error duplicating force package!',
			}
		);
	};

	const validateSettings = (settings: Echelon[], path: string[] = []) => {
		malformedPackagePath.current = [];

		for (const echelon of settings) {
			if (echelon.type > 1 && echelon.systems.length === 0) {
				malformedPackagePath.current = [...path, echelon.name];
				return false;
			}
			if (echelon.echelons.length > 0) {
				if (!validateSettings(echelon.echelons, [...path, echelon.name])) return false;
			}
		}

		return true;
	};

	useEffect(() => {
		initialize();
	}, [initialize]);

	return filters && inputData ? (
		<ForcePackagesPage
			forcePackageData={filters.force_packages as ForcePackagesFiltersData}
			inputData={inputData}
			initializeFilters={initialize}
			downloadForcePackageSettings={downloadForcePackageSettings}
			fpSettingsTemplate={fpSettingsTemplate}
			defaultSettings={defaultSettings}
			addForcePackage={addForcePackage}
			saveForcePackage={saveForcePackage}
			deleteForcePackage={deleteForcePackage}
			duplicateForcePackage={duplicateForcePackage}
		></ForcePackagesPage>
	) : (
		<Preloader></Preloader>
	);
};

export default ForcePackages;
